import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { PaDrawer } from "components/platform-admin";
import { createPlatformTenantUser } from "store/services/platformAdmin.services";
import {
  MANAGER_USER_TYPES,
  TENANT_USER_TYPE_OPTIONS,
  WORKER_USER_TYPES,
} from "./tenantDetail.utils";

const defaultValues = {
  user_type: "sp_manager",
  name: "",
  first_name: "",
  last_name: "",
  joining_date: "",
  job_title: "",
  phone: "",
  email: "",
  password: "",
};

const AddTenantUserDrawer = ({ open, onClose, tenantId, onCreated }) => {
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues });

  const userType = watch("user_type");
  const isManager = MANAGER_USER_TYPES.includes(userType);
  const isWorker = WORKER_USER_TYPES.includes(userType);

  useEffect(() => {
    if (!open) {
      reset(defaultValues);
    }
  }, [open, reset]);

  const submitHandler = async (values) => {
    try {
      const payload = {
        user_type: values.user_type,
        email: values.email.trim(),
        password: values.password,
      };

      if (isManager) {
        payload.name = values.name.trim();
      }

      if (isWorker) {
        payload.first_name = values.first_name.trim();
        payload.last_name = values.last_name.trim();
        payload.joining_date = values.joining_date;
        payload.job_title = values.job_title.trim();
        payload.phone = values.phone.trim();
      }

      const user = await createPlatformTenantUser(tenantId, payload);
      toast.success("Tenant user created");
      onCreated(user);
      onClose();
    } catch (error) {
      toast.error(error?.message || "Failed to create tenant user");
    }
  };

  return (
    <PaDrawer
      open={open}
      onClose={onClose}
      title="Add tenant user"
      description="Create a manager, HR officer, worker, or support coordinator in this tenant."
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="add-tenant-user-form"
            disabled={isSubmitting}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
          >
            {isSubmitting ? "Creating…" : "Create user"}
          </button>
        </div>
      }
    >
      <form id="add-tenant-user-form" className="space-y-4" onSubmit={handleSubmit(submitHandler)}>
        <div>
          <label className="block text-sm font-medium text-gray-700">User type</label>
          <select
            {...register("user_type", { required: true })}
            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          >
            {TENANT_USER_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {isManager ? (
          <div>
            <label className="block text-sm font-medium text-gray-700">Full name</label>
            <input
              type="text"
              {...register("name", { required: isManager })}
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
            {errors.name ? (
              <p className="mt-1 text-sm text-red-600">Name is required</p>
            ) : null}
          </div>
        ) : null}

        {isWorker ? (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700">First name</label>
                <input
                  type="text"
                  {...register("first_name", { required: isWorker })}
                  className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Last name</label>
                <input
                  type="text"
                  {...register("last_name", { required: isWorker })}
                  className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Joining date</label>
              <input
                type="date"
                {...register("joining_date", { required: isWorker })}
                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Job title</label>
              <input
                type="text"
                {...register("job_title", { required: isWorker })}
                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Phone</label>
              <input
                type="text"
                {...register("phone", { required: isWorker })}
                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
          </>
        ) : null}

        <div>
          <label className="block text-sm font-medium text-gray-700">Email</label>
          <input
            type="email"
            {...register("email", { required: true })}
            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Temporary password</label>
          <input
            type="password"
            {...register("password", { required: true, minLength: 8 })}
            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
          {errors.password ? (
            <p className="mt-1 text-sm text-red-600">Password must be at least 8 characters</p>
          ) : null}
        </div>
      </form>
    </PaDrawer>
  );
};

export default AddTenantUserDrawer;
